import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Pencil,
  Plus,
  Power,
  Search,
  SlidersHorizontal,
  Trash2,
  Warehouse as WarehouseIcon,
  X,
} from "lucide-react";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import FilterSelect from "src/components/ui/FilterSelect";
import PageSizeSelect from "src/components/ui/PageSizeSelect";
import Card from "src/components/common/card/Card";
import Popover from "src/components/ui/Popover";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import AddWarehouseModal from "src/components/modals/AddWarehouseModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as warehouseActions from "src/redux/slices/warehouseSlice";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";
import { Warehouse } from "src/types/warehouse.types";
import { toast } from "react-toastify";
import { formatCreatedBy } from "src/utils/common";

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const WarehouseListPage: React.FC = () => {
  const [limit, setLimit] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editRow, setEditRow] = useState<Warehouse | null>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusRow, setStatusRow] = useState<Warehouse | null>(null);
  const [statusUpdate, setStatusUpdate] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRow, setDeleteRow] = useState<Warehouse | null>(null);
  const [deleteRequested, setDeleteRequested] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"" | "active" | "inactive">("");

  const { warehouseList, editWarehouse, deleteWarehouse } = useAppSelector(
    (state) => state.warehouse
  );
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreate =
    isTenantAdmin || (permissions?.warehouses || []).includes("create");
  const canEdit =
    isTenantAdmin || (permissions?.warehouses || []).includes("edit");
  const canToggle =
    isTenantAdmin || (permissions?.warehouses || []).includes("delete");
  const { singular: warehouseLabel, plural: warehouseLabelPlural } = useWarehouseLabel();
  const dispatch = useAppDispatch();

  const rows: Warehouse[] = warehouseList?.data?.data || [];

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, limit]);

  useEffect(() => {
    if (editWarehouse.data && statusUpdate) {
      toast.success(`${warehouseLabel} updated successfully`);
      dispatch(warehouseActions.resetEditWarehouse());
      setStatusUpdate(false);
      handleStatusModalClose();
      getList(warehouseList?.data?.pagination?.currentPage || 1);
    } else if (editWarehouse.error && statusUpdate) {
      toast.error(`Error: ${editWarehouse.error}`);
      setStatusUpdate(false);
      handleStatusModalClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editWarehouse, dispatch, statusUpdate]);

  useEffect(() => {
    if (deleteWarehouse.data && deleteRequested) {
      toast.success(`${warehouseLabel} deleted successfully`);
      dispatch(warehouseActions.resetDeleteWarehouse());
      setDeleteRequested(false);
      handleDeleteModalClose();
      getList(1);
    } else if (deleteWarehouse.error && deleteRequested) {
      toast.error(`Error: ${deleteWarehouse.error}`);
      setDeleteRequested(false);
      handleDeleteModalClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteWarehouse, dispatch, deleteRequested]);

  const getList = (page: number) =>
    dispatch(
      warehouseActions.getWarehouseListRequest({
        page,
        limit,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
      })
    );

  const handlePageChange = (page: number) => getList(page);

  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatus("");
  };
  const hasActiveFilters = !!(search || status);

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusRow(null);
  };
  const onConfirmStatus = () => {
    if (!statusRow?._id) return;
    setStatusUpdate(true);
    dispatch(
      warehouseActions.editWarehouseRequest({
        _id: statusRow._id,
        status: statusRow.status === "active" ? "inactive" : "active",
      })
    );
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
    setDeleteRow(null);
  };
  const onConfirmDelete = () => {
    if (!deleteRow?._id) return;
    setDeleteRequested(true);
    dispatch(warehouseActions.deleteWarehouseRequest(deleteRow._id));
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setEditRow(null);
  };
  const handleModalSuccess = () => {
    handleModalClose();
    getList(warehouseList?.data?.pagination?.currentPage || 1);
  };

  return (
    <DashboardLayout
      meta={{
        title: `${warehouseLabelPlural} | Invyntra`,
        description: `Manage the ${warehouseLabelPlural.toLowerCase()} stock is tracked across.`,
        keywords: `${warehouseLabelPlural.toLowerCase()}, inventory, invyntra`,
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <WarehouseIcon size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {warehouseLabelPlural}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage the {warehouseLabelPlural.toLowerCase()} stock is tracked across.
              </p>
            </div>
          </div>

          {canCreate && (
            <Button rounded="full" onClick={() => setShowAddModal(true)}>
              <Plus size={16} className="mr-1" />
              Add {warehouseLabel}
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search
                </label>
                <Input
                  placeholder={`Search ${warehouseLabelPlural.toLowerCase()}...`}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>

              <FilterSelect
                variant="boxed"
                label="Status"
                value={status}
                onChange={(val) => setStatus(val as "" | "active" | "inactive")}
                allLabel="All statuses"
                options={STATUS_OPTIONS}
              />

              <div className="flex items-end">
                <Button
                  variant="outline"
                  color="danger"
                  rounded="full"
                  fullWidth
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <X size={15} className="mr-1.5" />
                  Clear filters
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* List */}
        <Card>
          <CardHeader
            title={`${warehouseLabel} list`}
            description={`Search and manage your ${warehouseLabelPlural.toLowerCase()}.`}
            actions={<PageSizeSelect value={limit} onChange={setLimit} />}
          />

          <TableV2
            columns={[
              {
                key: "name",
                header: "Name",
                accessor: ({ key, row }) => (
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {row[key]}
                  </span>
                ),
              },
              {
                key: "code",
                header: "Code",
                accessor: ({ row }) => row?.code || "-",
              },
              {
                key: "address",
                header: "Address",
                accessor: ({ row }) => row?.address || "-",
              },
              {
                key: "createdBy",
                header: "Created By",
                accessor: ({ row }) => formatCreatedBy(row?.createdBy),
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ key, row }) => (
                  <Badge
                    text={row?.[key] === "active" ? "Active" : "Inactive"}
                    color={row?.[key] === "active" ? "green" : "gray"}
                  />
                ),
              },
              {
                key: "actions",
                header: "Action",
                accessor: ({ row }) =>
                  !canEdit && !canToggle ? null : (
                    <Popover
                      trigger={
                        <button
                          className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                          title="Actions"
                        >
                          <MoreVertical size={16} />
                        </button>
                      }
                    >
                      {(close) => (
                        <div className="w-44 py-1.5">
                          {canEdit && (
                            <button
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                              onClick={() => {
                                close();
                                setEditRow(row);
                                setShowAddModal(true);
                              }}
                            >
                              <Pencil size={14} />
                              Edit
                            </button>
                          )}
                          {canToggle && (
                            <button
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                              onClick={() => {
                                close();
                                setStatusRow(row);
                                setShowStatusModal(true);
                              }}
                            >
                              <Power size={14} />
                              {row.status === "active" ? "Deactivate" : "Activate"}
                            </button>
                          )}
                          {canToggle && (
                            <button
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                              onClick={() => {
                                close();
                                setDeleteRow(row);
                                setShowDeleteModal(true);
                              }}
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </Popover>
                  ),
              },
            ]}
            data={rows}
            pagination={warehouseList?.data?.pagination}
            onPageChange={handlePageChange}
            loading={warehouseList.loading}
          />
        </Card>
      </div>

      {showAddModal && (
        <AddWarehouseModal
          warehouse={editRow}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {showStatusModal && statusRow?._id && (
        <ConfirmationModal
          title={
            statusRow.status === "active"
              ? `Deactivate ${warehouseLabel.toLowerCase()}`
              : `Activate ${warehouseLabel.toLowerCase()}`
          }
          message={`Are you sure you want to ${
            statusRow.status === "active" ? "deactivate" : "activate"
          } "${statusRow.name}"?`}
          onConfirm={onConfirmStatus}
          onCancel={handleStatusModalClose}
          open={showStatusModal}
          isLoading={editWarehouse.loading}
        />
      )}

      {showDeleteModal && deleteRow?._id && (
        <ConfirmationModal
          title={`Delete ${warehouseLabel.toLowerCase()}`}
          message={
            <>
              Are you sure you want to delete{" "}
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {deleteRow.name}
              </span>
              ? This action cannot be undone.
            </>
          }
          variant="danger"
          confirmLabel="Delete"
          onConfirm={onConfirmDelete}
          onCancel={handleDeleteModalClose}
          open={showDeleteModal}
          isLoading={deleteWarehouse.loading}
        />
      )}
    </DashboardLayout>
  );
};

export default WarehouseListPage;
